import type { Address, } from "@bracket/chainography";
import type { ValidChainId, } from "@bracket/types";
import type { Config, } from "@wagmi/core";
import type { defineChain, PublicClient, WalletClient, } from "viem";

import {
	getPublicClient,
	getWalletClient,
	getAccount,
} from "wagmi/actions";
import { getViemClient } from "./getViemClient";
import { getViemWalletClient } from "./getViemWalletClient";
import { runtime } from "./helpers";


export declare type GetViemClientsProps = {
	chainId?: ValidChainId;
	config?: Config;
	chain?: ReturnType<typeof defineChain>;
	key?: Address;
};

/**
 * get both a public and wallet client
 * @param {ValidChainId?} chainId the chain the clients should be configured for, default 1
 * @param {chain} chain (deprecating) the chain instance, returned from defineChain
 * @param {Config} config a wagmi compatible chain configuration to use for these clients, required if used in a browser
 * @param {string} key the private key to use for the wallet client, if not provided the private key will be read from the environment
 * @returns 
 */
export const getViemClients = async ({
	chainId = 1,
	chain,
	config,
	key,
}: GetViemClientsProps = {}): Promise<[PublicClient, WalletClient]> => {

	try {
		const rt = runtime();
		let writable: WalletClient;
		let readable: PublicClient;
		if (rt === "NODE") {
			readable = getViemClient({ chain, chainId, });
			writable = getViemWalletClient({ chain, chainId, key, });
		} else if (rt === "BROWSER") {
			if(!config)
				throw new Error("browser/wagmi must send the wagmi config");
			const { status } = config.state;
			if(status !== "connected") {
				await awaitConnectedStatus(config).catch(err => {
					console.warn("ERROR.... :///", err);
				})
			}
			readable = getPublicClient(config, { chainId, });
			writable = await getWalletClient(config, { chainId, });
		} else {
			throw new Error(`@bracket/chainography::getViemClients recieved unknown runtime "${runtime()}"`);
		}
		if(readable && writable) {
			return [readable, writable];
		}
		throw new Error("failed to produce a readable client");
	} catch (err) {
		throw new Error(
			`@bracket/contractor::getViemClients failed - ${err.message}`,
		);
	}
};

const awaitConnectedStatus = (config: Config) => {
	return new Promise((resolve, reject) => {
		const { state: { status } } = config;
		if(status === "connected") {
			return resolve(true);
		}
		const to = setTimeout(() => {
			clearInterval(inte);
			clearTimeout(to);
			reject(new Error("failed to connect to wallet"));
		}, 10_000);
		const inte = setInterval(async () => {
			const { state: { status } } = config;
			if(status === "connected") {
				clearTimeout(to);
				clearInterval(inte);
				resolve(true);
			}
		}, 199);
	});
};
